import { useLazyQuery } from '@apollo/client';
import { Select } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QUERY_COUNTRY_CITYS } from '../queries';
import { message, Divider, Icon } from 'antd';
import { uniqBy } from 'lodash';

/**
 * Renders a city selector by country component with async data fetching.
 * if you want a pure city selector, find it in components dir.
 *
 * @param {Object} props - The props object.
 * @param {string} props.value - The selected value.
 * @param {function} props.onChange - The function called when the value changes.
 * @param {string} props.selectedCountry - The selected country.
 * @param {function} props.onBlur - The function called when the component loses focus.
 * @param {boolean} props.disabled - Determines if the component is disabled.
 * @param {string} props.placeholder - The placeholder text.
 * @param {Array} props.defaultCitiesList - The default list of cities.
 * @return {JSX.Element} The city selector component.
 */
const AsyncCitySelector = ({
  value,
  onChange,
  selectedCountry,
  onBlur,
  disabled,
  placeholder,
  defaultCitiesList,
}) => {
  const { t } = useTranslation();
  const prevSelectedCountrySlug = useRef(selectedCountry);
  const [cityList, setCityList] = useState(defaultCitiesList || []);
  const [pagination, setPagination] = useState({ pageNumber: 1, searchSlug: null });
  const [queryCities, { loading, data }] = useLazyQuery(QUERY_COUNTRY_CITYS, {
    variables: {
      slug: selectedCountry,
      cityPageNumber: pagination.pageNumber,
      searchCitySlug: pagination.searchSlug ? [pagination.searchSlug] : null,
    },
    onError: e => {
      message.error(e?.toString());
    },
  });

  useEffect(() => {
    if (selectedCountry !== prevSelectedCountrySlug.current) {
      setPagination({ pageNumber: 1, searchSlug: null });
      setCityList([]);
      prevSelectedCountrySlug.current = selectedCountry;
    }
    queryCities();
  }, [selectedCountry]);

  useEffect(() => {
    setCityList(newestCityList =>
      uniqBy(
        [
          ...newestCityList,
          ...(data?.country?.cities?.edges.map(e => ({
            id: e.node.id,
            slug: e.node.slug,
            name: e.node.name,
          })) || []),
        ],
        'id',
      ),
    );
  }, [data]);

  return (
    <Select
      loading={loading}
      onBlur={onBlur}
      showSearch
      optionFilterProp={'title'}
      mode="multiple"
      placeholder={placeholder || t('cms.referral.promotion.modal.city_selector.placeholder')}
      value={value}
      disabled={disabled}
      onSearch={searchSlug =>
        setPagination(() => ({
          pageNumber: 1,
          searchSlug: searchSlug,
        }))
      }
      allowClear
      onPopupScroll={e => {
        e.persist();
        const target = e.target;
        if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
          if (data?.country?.cities?.pageInfo.hasNextPage) {
            setPagination(currentPagination => ({
              ...currentPagination,
              pageNumber: currentPagination.pageNumber + 1,
            }));
          }
        }
      }}
      dropdownRender={menu => (
        <div>
          {menu}
          {loading && (
            <>
              <Divider style={{ margin: '4px 0' }} />
              <div style={{ padding: '4px 8px' }} onMouseDown={e => e.preventDefault()}>
                <Icon type="loading" /> Loading
              </div>
            </>
          )}
        </div>
      )}
      onChange={v => {
        onChange(v);
      }}
    >
      <For of={cityList} each="data" index="index">
        <Select.Option key={data.id} title={data.slug} value={data.id}>
          {data.name}
        </Select.Option>
      </For>
    </Select>
  );
};

export default AsyncCitySelector;
